import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_LOCATION } from 'config/defaultLocation.config';
import Cookies from 'js-cookie';
import { selectUserPlace } from 'store/thunks/User/userLocationThunks';

const initialSelectedPlaceId = Cookies.get('selectedPlaceId') || DEFAULT_LOCATION.place_id;
const isDefaultSelectedPlace = initialSelectedPlaceId === DEFAULT_LOCATION.place_id;

const userSlice = createSlice({
  name: 'user',
  initialState: {
    username: '', // The username of the currently logged-in user
    email: '', // The email of the currently logged-in user
    organization: '', // The name of the organization the user belongs to
    selectedLocation: {
      placeId: initialSelectedPlaceId, // The place ID of the user's selected location
      name: DEFAULT_LOCATION.title || DEFAULT_LOCATION.term, // The name of the user's selected location
      city: DEFAULT_LOCATION.city,
      state: DEFAULT_LOCATION.state_long,
      latitude: DEFAULT_LOCATION.latitude,
      longitude: DEFAULT_LOCATION.longitude,
      city_ids: DEFAULT_LOCATION.city_ids ?? [], // All CW-confirmed city ids for the selected location (wide net)
      source: isDefaultSelectedPlace ? 'default' : 'user', // Indicates if using the default location or a user-selected location
      status: isDefaultSelectedPlace ? 'ready' : 'idle', // Status of the selected location (e.g., 'checking', 'ready', 'error')
      statusMessage: '', // Human-readable progress/error text streamed from the readiness check (subscribe SSE)
    },
  },
  reducers: {
    setUserEmail: (state, action) => {
      state.email = action.payload;

      // If email is empty, remove the cookie to avoid stale data
      if (!action.payload) {
        Cookies.remove('email');
        return;
      }

      Cookies.set('email', action.payload, { expires: 365 });
    },
    setUsername: (state, action) => {
      state.username = action.payload;

      // If username is empty, remove the cookie to avoid stale data
      if (!action.payload) {
        Cookies.remove('username');
        return;
      }

      Cookies.set('username', action.payload);
    },
    setUserOrganization: (state, action) => {
      state.organization = action.payload;

      // If organization is empty, remove the cookie to avoid stale data
      if (!action.payload) {
        Cookies.remove('organizationName');
        return;
      }

      Cookies.set('organizationName', action.payload);
    },
    setUserSelectedPlaceId: (state, action) => {
      const placeId = action.payload;
      state.selectedLocation.placeId = placeId;
      state.selectedLocation.source = 'user';
      Cookies.set('selectedPlaceId', placeId);
    },
    // Streams readiness progress/error text from the subscribe SSE into Redux
    // so the UI can show "Waiting for data in <location>…" etc.
    setSelectedLocationStatus: (state, action) => {
      const { status, statusMessage } = action.payload;
      if (status !== undefined) state.selectedLocation.status = status;
      if (statusMessage !== undefined) state.selectedLocation.statusMessage = statusMessage;
    },
    setUserIdentity: (state, action) => {
      const { email, username, organization } = action.payload;
      // Store in Redux for reactive access across the app
      state.email = email;
      state.username = username;
      state.organization = organization;

      // If all identity fields are empty, clear cookies to avoid stale data
      if (!email && !username && !organization) {
        Cookies.remove('email');
        Cookies.remove('username');
        Cookies.remove('organizationName');
        return;
      }

      // Persist user identity in cookies for cross-session access
      Cookies.set('email', email, { expires: 365 });
      Cookies.set('username', username, { expires: 365 });
      Cookies.set('organizationName', organization);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(selectUserPlace.pending, (state) => {
        state.selectedLocation.status = 'checking';
        state.selectedLocation.statusMessage = 'Preparing weather data for this location…';
      })
      .addCase(selectUserPlace.fulfilled, (state, action) => {
        const {
          placeId,
          title,
          city,
          state: locationState,
          latitude,
          longitude,
          city_ids,
        } = action.payload;
        state.selectedLocation = {
          placeId,
          name: title,
          city,
          state: locationState,
          latitude,
          longitude,
          city_ids: city_ids ?? [],
          source: 'user',
          status: 'ready',
          statusMessage: '',
        };
      })
      .addCase(selectUserPlace.rejected, (state, action) => {
        state.selectedLocation.status = 'error';
        state.selectedLocation.statusMessage = action.payload || 'Unable to prepare location';
        console.error('Error preparing location:', action.payload || 'Unknown error');
      });
  },
});

export const {
  setUserEmail,
  setUsername,
  setUserOrganization,
  setUserSelectedPlaceId,
  setSelectedLocationStatus,
  setUserIdentity,
} = userSlice.actions;

export default userSlice.reducer;
